import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, FileSpreadsheet, FileText, Loader2, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { RANGE_PRESETS, computeRange, money, num } from './_shared';

const MODES = [['cash', 'Cash'], ['credit', 'Credit'], ['card', 'Card'], ['upi', 'UPI']];
const dmy = (d) => d ? d.split('-').reverse().join('-') : '';

export default function PaymentModeReport() {
  const { toast } = useToast();
  const [preset, setPreset] = useState('last30');
  const init = computeRange('last30');
  const [dateFrom, setDateFrom] = useState(init.date_from);
  const [dateTo, setDateTo] = useState(init.date_to);
  const [storeId, setStoreId] = useState('all');
  const [stores, setStores] = useState([]);
  const [modes, setModes] = useState(MODES.map(m => m[0]));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  useEffect(() => { api.get('/stores').then(r => setStores(r.data?.stores || r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (preset !== 'custom') { const r = computeRange(preset); setDateFrom(r.date_from); setDateTo(r.date_to); } }, [preset]);

  const params = useCallback(() => ({ date_from: dateFrom, date_to: dateTo, store_id: storeId, modes: modes.length === MODES.length ? 'all' : modes.join(',') }), [dateFrom, dateTo, storeId, modes]);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/reports/sales/payment-mode', { params: params() })).data); }
    catch (e) { toast({ title: 'Load failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [params, toast]);
  useEffect(() => { load(); }, [load]);

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      const res = await api.get(`/reports/sales/payment-mode/${kind}`, { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a');
      a.href = url; a.download = (res.headers['content-disposition'] || '').split('filename=')[1]?.replace(/"/g, '') || `payment-mode-sales.${kind}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Export failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setExporting(''); }
  };

  const toggleMode = (m) => setModes(cur => cur.includes(m) ? (cur.length > 1 ? cur.filter(x => x !== m) : cur) : [...cur, m]);
  const shown = MODES.filter(m => modes.includes(m[0]));
  const h = data?.header;
  const t = data?.totals;

  return (
    <div className="p-6" data-testid="payment-mode-report">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold flex items-center gap-2"><Wallet className="w-5 h-5" /> Payment Mode wise Sales</h1>
        <div className="flex gap-2 mr-14">
          <Button variant="outline" size="sm" onClick={() => exportFile('xlsx')} disabled={!!exporting || !data} data-testid="pm-export-xlsx">{exporting === 'xlsx' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Excel</Button>
          <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} disabled={!!exporting || !data} data-testid="pm-export-pdf">{exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} PDF</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 pb-4 border-b border-gray-200 mb-4">
        <div className="min-w-[9rem]"><label className="block text-xs text-gray-500 mb-1">Range</label>
          <Select value={preset} onValueChange={setPreset}><SelectTrigger data-testid="pm-range"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_PRESETS.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="block text-xs text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreset('custom'); }} className="w-36" data-testid="pm-from" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreset('custom'); }} className="w-36" data-testid="pm-to" /></div>
        <div className="min-w-[13rem]"><label className="block text-xs text-gray-500 mb-1">Branch</label>
          <Select value={storeId} onValueChange={setStoreId}><SelectTrigger data-testid="pm-branch"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Branches</SelectItem>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}{s.store_type === 'franchise' ? ' (Franchise)' : ''}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="block text-xs text-gray-500 mb-1">Pay modes</label>
          <Popover><PopoverTrigger asChild><Button variant="outline" className="h-10 min-w-[10rem] justify-start font-normal" data-testid="pm-modes">{modes.length === MODES.length ? 'ALL' : shown.map(m => m[1]).join(', ')}</Button></PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="start">
              <label className="flex items-center gap-2 text-sm py-1 cursor-pointer"><Checkbox checked={modes.length === MODES.length} onCheckedChange={() => setModes(MODES.map(m => m[0]))} data-testid="pm-mode-all" /> ALL</label>
              {MODES.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm py-1 cursor-pointer"><Checkbox checked={modes.includes(k)} onCheckedChange={() => toggleMode(k)} data-testid={`pm-mode-${k}`} /> {l}</label>)}
            </PopoverContent></Popover></div>
        <div className="ml-auto"><Button variant="outline" size="sm" onClick={load} data-testid="pm-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button></div>
      </div>

      {h && (
        <div className="rounded-lg border bg-gray-50 p-3 mb-4 text-sm" data-testid="pm-header">
          {h.scope === 'store' ? (
            <>
              <div className="font-semibold">{h.code} – {h.name} <Badge variant="outline" className="ml-1 text-[10px]">{h.store_type === 'franchise' ? `Franchise ${h.franchise_code || ''}` : 'Own branch'}</Badge></div>
              <div className="text-xs text-gray-600">{h.address}</div>
              <div className="text-xs text-gray-600">{[h.gstin && `GSTIN ${h.gstin}`, h.phone && `Ph ${h.phone}`, h.email].filter(Boolean).join(' · ')}</div>
            </>
          ) : (
            <>
              <div className="font-semibold">All Branches / Franchises ({h.count})</div>
              <div className="text-xs text-gray-600 truncate" title={h.branches?.join(', ')}>{h.branches?.join(', ')}</div>
            </>
          )}
          <div className="text-xs text-gray-500 mt-1">Period {dmy(data.range.date_from)} to {dmy(data.range.date_to)} · Pay modes: {modes.length === MODES.length ? 'All' : shown.map(m => m[1]).join(', ')}</div>
        </div>
      )}

      <div className="rounded-lg border overflow-x-auto bg-white">
        <table className="w-full text-sm" data-testid="pm-table">
          <thead className="bg-gray-100 text-xs text-gray-600"><tr>
            <th className="p-2 text-left">Date</th><th className="p-2 text-right">Bills</th>
            {shown.map(m => <th key={m[0]} className="p-2 text-right">{m[1]}</th>)}
            <th className="p-2 text-right">Total</th></tr></thead>
          <tbody>
            {loading && !data && <tr><td colSpan={3 + shown.length} className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
            {data && !data.rows.length && <tr><td colSpan={3 + shown.length} className="p-8 text-center text-gray-400" data-testid="pm-empty">No collections in this range</td></tr>}
            {data?.rows.map(r => (
              <tr key={r.date} className="border-t" data-testid={`pm-row-${r.date}`}>
                <td className="p-2 font-mono">{dmy(r.date)}</td><td className="p-2 text-right">{num(r.bills)}</td>
                {shown.map(m => <td key={m[0]} className="p-2 text-right font-mono">{money(r[m[0]])}</td>)}
                <td className="p-2 text-right font-mono font-semibold">{money(r.total)}</td>
              </tr>
            ))}
          </tbody>
          {t && data.rows.length > 0 && (
            <tfoot className="bg-gray-50 font-semibold"><tr className="border-t-2" data-testid="pm-totals">
              <td className="p-2">TOTAL</td><td className="p-2 text-right">{num(t.bills)}</td>
              {shown.map(m => <td key={m[0]} className="p-2 text-right font-mono">{money(t[m[0]])}</td>)}
              <td className="p-2 text-right font-mono">{money(t.total)}</td></tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
